import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { Button, Input } from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { Alert } from '@vnetwork/ui/feedback';
import { PasswordInput } from './AuthShell';
import { setMigrationPassword } from '@/api/auth-flow';
import { brand } from '@/config/brand';
import { startSsoLogin } from '@/lib/sso';

interface Props {
  email: string;
  migrationToken: string;
  /** Where to land after SSO sign-in (carried from the login redirect). */
  from?: string;
  /** Reconciliation onboarding: also collect the contact's name/phone. */
  collectContact?: boolean;
  customerId?: number;
  prefillName?: string;
  prefillPhone?: string;
}

interface FormValues {
  new_password: string;
  new_password_confirmation: string;
  name: string;
  phone: string;
}

export function MigrationSetPasswordForm({
  email,
  migrationToken,
  from = '/',
  collectContact = false,
  customerId,
  prefillName = '',
  prefillPhone = '',
}: Props) {
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      new_password: '',
      new_password_confirmation: '',
      name: prefillName,
      phone: prefillPhone,
    },
  });

  const { mutateAsync, isPending } = useMutation({ mutationFn: setMigrationPassword });

  const onSubmit = async (values: FormValues) => {
    setError(null);
    try {
      await mutateAsync({
        migration_token: migrationToken,
        new_password: values.new_password,
        new_password_confirmation: values.new_password_confirmation,
        ...(collectContact
          ? { name: values.name, phone: values.phone, customer_id: customerId }
          : {}),
      });
      setDone(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not complete migration.');
    }
  };

  const continueToSso = async () => {
    try {
      await startSsoLogin(from); // redirects to Keycloak, returns to the intended page
    } catch {
      setError('Could not start sign-in. Please try again.');
    }
  };

  if (done) {
    return (
      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        <Alert tone="success" title="Migration complete">
          Your account now uses {brand.ssoName}. Sign in with your new password to
          continue.
        </Alert>
        <Button type="button" variant="primary" fullWidth onClick={continueToSso}>
          Continue to sign in
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      {error ? <Alert tone="danger" title={error} /> : null}

      {collectContact ? (
        <>
          <Field label="Contact name" error={errors.name?.message}>
            {(p) => (
              <Input
                {...p}
                placeholder="Your name"
                autoComplete="name"
                {...register('name')}
              />
            )}
          </Field>
          <Field label="Contact phone" error={errors.phone?.message}>
            {(p) => (
              <Input
                {...p}
                type="tel"
                placeholder="Phone number"
                autoComplete="tel"
                {...register('phone')}
              />
            )}
          </Field>
        </>
      ) : null}

      <Field
        label="New password"
        required
        description={`For ${email}`}
        error={errors.new_password?.message}
      >
        {(p) => (
          <PasswordInput
            {...p}
            autoComplete="new-password"
            invalid={!!errors.new_password}
            {...register('new_password', {
              required: 'Password is required.',
              minLength: { value: 9, message: 'Password must be 9–64 characters.' },
              maxLength: { value: 64, message: 'Password must be 9–64 characters.' },
            })}
          />
        )}
      </Field>

      <Field
        label="Confirm new password"
        required
        error={errors.new_password_confirmation?.message}
      >
        {(p) => (
          <PasswordInput
            {...p}
            autoComplete="new-password"
            invalid={!!errors.new_password_confirmation}
            {...register('new_password_confirmation', {
              required: 'Please confirm your password.',
              validate: (v) => v === watch('new_password') || 'Passwords do not match.',
            })}
          />
        )}
      </Field>

      <Button type="submit" variant="primary" fullWidth loading={isPending}>
        Set password & continue
      </Button>
    </form>
  );
}
